import { useState } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

interface JobPosition {
  id: string;
  title: string;
  department: string;
  location: string;
  type: string;
  description: string;
  requirements: string[];
  responsibilities: string[];
}

export default function CareersPage() {
  const [selectedJob, setSelectedJob] = useState<JobPosition | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: 'IT Sales Manager',
    experience: '',
    linkedin: '',
    coverLetter: '',
    availability: '',
    agreeToPrivacy: false
  });
  const [charCount, setCharCount] = useState(0);
  const [fileName, setFileName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const jobPositions: JobPosition[] = [
    {
      id: '1',
      title: 'IT Sales Manager',
      department: 'Sales',
      location: 'Remote (Pakistan)',
      type: 'Full-time',
      description: 'Lead our European sales expansion efforts and drive revenue growth for our innovative digital solutions. Build strong client relationships and develop the European market for our fast-growing tech startup.',
      requirements: [
        'Proven experience in IT sales, B2B SaaS, or tech-related business development',
        'Strong understanding of the European market',
        'Excellent communication and negotiation skills',
        'Ability to build trust and maintain long-term client relationships',
        'Self-driven, proactive, and comfortable in a startup environment',
        'Fluent in English; additional European languages are a plus'
      ],
      responsibilities: [
        'Proactively reach out to prospects via email, calls, LinkedIn, and networking',
        'Build and manage a qualified sales pipeline across the full sales cycle',
        'Deliver product presentations and online demos to prospective clients',
        'Negotiate contracts and close deals to achieve monthly and quarterly targets',
        'Grow and retain a portfolio of clients with high satisfaction',
        'Collaborate with marketing and product teams to refine messaging and positioning',
        'Track sales metrics, prepare progress reports, and recommend improvements'
      ]
    },
    {
      id: '2',
      title: 'Solutions Engineer - Enterprise',
      department: 'Solutions & Consulting',
      location: 'Remote (Pakistan)',
      type: 'Full-time',
      description: 'Act as the subject matter expert (SME) for all IT solutions and consulting engagements. Advise customers on designing and implementing comprehensive, scalable, and future-proof IT environments. Bridge business requirements with technical solutions and play a key role in solution architecture, presales support, and customer advisory.',
      requirements: [
        'Proven experience as a Solutions Engineer, Solutions Architect, or IT Consultant',
        'Strong background in comprehensive IT solutions and IT architecture',
        'Hands-on knowledge of cloud platforms (AWS, Azure, GCP or similar)',
        'Experience with on-premise and hybrid infrastructure',
        'Expertise in networking, security, and virtualization',
        'Experience advising customers on long-term IT strategy and modernization',
        'Excellent problem-solving and analytical skills',
        'Strong communication skills with the ability to explain complex technical concepts to both technical and non-technical stakeholders',
        'Ability to work independently in a remote environment',
        'Relevant certifications (e.g., AWS, Azure, Cisco, VMware, TOGAF) - Nice to have',
        'Experience working with international clients - Nice to have',
        'Exposure to DevOps, automation, or modern application architectures - Nice to have'
      ],
      responsibilities: [
        'Act as the primary subject matter expert for IT solutions and IT consulting',
        'Design and propose end-to-end IT solutions, including infrastructure, cloud, networking, security, and hybrid environments',
        'Advise customers on future-proof IT architectures aligned with their business goals and growth plans',
        'Engage with customers to gather requirements, assess existing environments, and recommend optimized solutions',
        'Develop solution architectures, technical documentation, and high-level designs',
        'Support presales activities including solution presentations, proposals, and technical workshops',
        'Collaborate with internal teams to ensure successful implementation and delivery of solutions',
        'Stay current with emerging technologies and industry best practices to continuously improve solution offerings'
      ]
    }
  ];

  const handleCoverLetterChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= 1500) {
      setFormData({ ...formData, coverLetter: text });
      setCharCount(text.length);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const maxSize = 5 * 1024 * 1024; // 5MB
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      
      if (file.size > maxSize) {
        alert('File size must be less than 5MB');
        e.target.value = '';
        return;
      }
      
      if (!allowedTypes.includes(file.type)) {
        alert('Only PDF, DOC, and DOCX files are allowed');
        e.target.value = '';
        return;
      }
      
      setFileName(file.name);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.agreeToPrivacy) {
      alert('Please agree to the Privacy Policy to continue');
      return;
    }

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.experience || !formData.coverLetter || !formData.availability || !fileName) {
      alert('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const form = e.target as HTMLFormElement;
      const formDataToSend = new FormData(form);

      const response = await fetch('https://readdy.ai/api/form/d51ti01n1516q9jm3skg', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          position: 'IT Sales Manager',
          experience: '',
          linkedin: '',
          coverLetter: '',
          availability: '',
          agreeToPrivacy: false
        });
        setCharCount(0);
        setFileName('');
        form.reset();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: 'ri-money-euro-circle-line',
      title: 'Competitive Salary',
      description: 'Industry-leading compensation packages with performance bonuses'
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Health Insurance',
      description: 'Comprehensive health, dental, and vision coverage for you and your family'
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Flexible Schedule',
      description: 'Work-life balance with flexible hours and remote work options'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Learning & Development',
      description: 'Continuous training, certifications, and professional development opportunities'
    },
    {
      icon: 'ri-plane-line',
      title: 'Paid Time Off',
      description: 'Generous vacation days, sick leave, and paid holidays'
    },
    {
      icon: 'ri-team-line',
      title: 'Team Events',
      description: 'Regular team building activities, social events, and company outings'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Hero Section */}
      <section className="relative text-white pt-32 pb-24">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/d9e15d752d95c8208bec0893842432fc.jpg"
            alt="Thyamis Office"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Join Our Team at Thyamis
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Build your career with a leading IT solutions company. We're looking for passionate professionals who want to make a difference in the technology industry and grow with our innovative team.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="#open-positions"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                View Open Positions
              </a>
              <a
                href="#benefits"
                className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                Our Benefits
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Join Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">Our Company Culture & Values</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              At Thyamis, we believe in fostering an environment where innovation thrives, collaboration is valued, and every team member can reach their full potential.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {/* Innovation */}
            <div className="bg-blue-50 p-8 rounded-2xl text-center">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <i className="ri-lightbulb-line text-3xl text-blue-500"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Innovation</h3>
              <p className="text-gray-600 leading-relaxed">
                We encourage creative thinking and embrace new technologies to deliver cutting-edge solutions for our clients.
              </p>
            </div>

            {/* Collaboration */}
            <div className="bg-green-50 p-8 rounded-2xl text-center">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <i className="ri-team-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Collaboration</h3>
              <p className="text-gray-600 leading-relaxed">
                Teamwork is at the heart of everything we do. We believe diverse perspectives lead to better outcomes.
              </p>
            </div>

            {/* Excellence */}
            <div className="bg-yellow-50 p-8 rounded-2xl text-center">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <i className="ri-star-line text-3xl text-orange-500"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Excellence</h3>
              <p className="text-gray-600 leading-relaxed">
                We strive for excellence in everything we do, from client service to personal development and growth.
              </p>
            </div>

            {/* Growth */}
            <div className="bg-purple-50 p-8 rounded-2xl text-center">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-6 mx-auto">
                <i className="ri-arrow-up-line text-3xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Growth</h3>
              <p className="text-gray-600 leading-relaxed">
                We invest in our people's professional development and provide opportunities for career advancement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Work Environment & Team Section */}
      <section id="benefits" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">Work Environment & Team</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Experience our collaborative work environment where innovation meets teamwork in a modern, supportive setting.
            </p>
          </div>

          {/* Modern Workspace */}
          <div className="max-w-6xl mx-auto mb-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="w-full h-96 rounded-lg overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20collaborative%20office%20workspace%20with%20open%20floor%20plan%20natural%20lighting%20contemporary%20furniture%20technology%20equipment%20professional%20business%20environment%20clean%20minimalist%20design%20bright%20airy%20atmosphere%20team%20working%20spaces&width=600&height=400&seq=careers-workspace-main&orientation=landscape"
                  alt="Modern Workspace"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="text-3xl font-bold mb-6">Modern Workspace</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Our state-of-the-art office features open collaborative spaces, quiet focus areas, and cutting-edge technology to support your best work. We've designed our workspace to foster creativity, productivity, and innovation.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Open collaboration areas for team brainstorming</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Latest technology and equipment</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 text-xl mr-3 mt-1"></i>
                    <span className="text-gray-700">Comfortable break areas and amenities</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Team Activities */}
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                <div className="w-full h-48 overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=diverse%20business%20team%20collaboration%20meeting%20brainstorming%20session%20modern%20office%20professional%20environment%20people%20working%20together%20discussing%20ideas%20technology%20workspace&width=400&height=300&seq=careers-team-collab&orientation=landscape"
                    alt="Team Collaboration"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-xl font-bold mb-3">Team Collaboration</h4>
                  <p className="text-gray-600 leading-relaxed">
                    Regular team meetings, brainstorming sessions, and collaborative projects that bring out the best in everyone.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                <div className="w-full h-48 overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=corporate%20team%20building%20activities%20office%20social%20events%20colleagues%20celebrating%20together%20fun%20workplace%20culture%20professional%20environment%20happy%20employees%20group%20activities&width=400&height=300&seq=careers-team-events&orientation=landscape"
                    alt="Team Events"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-xl font-bold mb-3">Team Events</h4>
                  <p className="text-gray-600 leading-relaxed">
                    From team building activities to social gatherings, we create opportunities for meaningful connections.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                <div className="w-full h-48 overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=professional%20mentorship%20business%20coaching%20senior%20mentor%20guiding%20junior%20employee%20office%20setting%20knowledge%20transfer%20career%20development%20learning%20training%20workplace&width=400&height=300&seq=careers-mentorship&orientation=landscape"
                    alt="Mentorship"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h4 className="text-xl font-bold mb-3">Mentorship</h4>
                  <p className="text-gray-600 leading-relaxed">
                    Structured mentorship programs that help you grow professionally and achieve your career goals.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">Apply for a Position</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Ready to join our team? Fill out the application form below and take the first step toward an exciting career at Thyamis.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <form id="career-application-form" data-readdy-form onSubmit={handleSubmit}>
                {/* Success/Error Messages */}
                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
                    <i className="ri-checkbox-circle-line text-xl mr-2"></i>
                    Application submitted successfully! We'll review your application and get back to you soon.
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
                    <i className="ri-error-warning-line text-xl mr-2"></i>
                    There was an error submitting your application. Please try again.
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Enter your first name"
                    />
                  </div>

                  <div>
                    <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="+92 XXX XXXXXXX"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="position" className="block text-sm font-semibold text-gray-700 mb-2">
                    Position of Interest <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="position"
                    name="position"
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                  >
                    <option value="IT Sales Manager">IT Sales Manager</option>
                    <option value="Solutions Engineer - Enterprise">Solutions Engineer - Enterprise</option>
                  </select>
                </div>

                <div className="mb-6">
                  <label htmlFor="experience" className="block text-sm font-semibold text-gray-700 mb-2">
                    Years of Experience <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="e.g., 3-5 years"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="linkedin" className="block text-sm font-semibold text-gray-700 mb-2">
                    LinkedIn Profile (Optional)
                  </label>
                  <input
                    type="url"
                    id="linkedin"
                    name="linkedin"
                    value={formData.linkedin}
                    onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="https://linkedin.com/in/yourprofile"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="coverLetter" className="block text-sm font-semibold text-gray-700 mb-2">
                    Cover Letter <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="coverLetter"
                    name="coverLetter"
                    value={formData.coverLetter}
                    onChange={handleCoverLetterChange}
                    required
                    rows={6}
                    maxLength={1500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                    placeholder="Tell us why you're interested in this position and what makes you a great fit..."
                  ></textarea>
                  <div className="text-right text-sm text-gray-500 mt-1">
                    {charCount}/1500 characters
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="resume" className="block text-sm font-semibold text-gray-700 mb-2">
                    Resume/CV <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      id="resume"
                      name="resume"
                      onChange={handleFileChange}
                      required
                      accept=".pdf,.doc,.docx"
                      className="hidden"
                    />
                    <label
                      htmlFor="resume"
                      className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-teal-500 transition-colors bg-gray-50"
                    >
                      {fileName ? (
                        <div className="text-center">
                          <i className="ri-file-text-line text-4xl text-teal-500 mb-2"></i>
                          <p className="text-sm font-medium text-gray-700">{fileName}</p>
                          <p className="text-xs text-gray-500 mt-1">Click to change file</p>
                        </div>
                      ) : (
                        <div className="text-center">
                          <i className="ri-upload-cloud-line text-4xl text-gray-400 mb-2"></i>
                          <p className="text-sm font-medium text-gray-700">Click to upload your resume or drag and drop</p>
                          <p className="text-xs text-gray-500 mt-1">PDF, DOC, DOCX up to 5MB</p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Availability <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="availability"
                        value="Immediate"
                        checked={formData.availability === 'Immediate'}
                        onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
                        required
                        className="w-4 h-4 text-teal-500 focus:ring-teal-500 mt-1 cursor-pointer"
                      />
                      <span className="ml-3 text-sm text-gray-700">Immediate</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="availability"
                        value="2 Weeks Notice"
                        checked={formData.availability === '2 Weeks Notice'}
                        onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
                        className="w-4 h-4 text-teal-500 focus:ring-teal-500 cursor-pointer"
                      />
                      <span className="ml-3 text-sm text-gray-700">2 Weeks Notice</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="availability"
                        value="1 Month"
                        checked={formData.availability === '1 Month'}
                        onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
                        className="w-4 h-4 text-teal-500 focus:ring-teal-500 cursor-pointer"
                      />
                      <span className="ml-3 text-sm text-gray-700">1 Month</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="availability"
                        value="Negotiable"
                        checked={formData.availability === 'Negotiable'}
                        onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
                        className="w-4 h-4 text-teal-500 focus:ring-teal-500 cursor-pointer"
                      />
                      <span className="ml-3 text-sm text-gray-700">Negotiable</span>
                    </label>
                  </div>
                </div>

                <div className="mb-8">
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      name="agreeToPrivacy"
                      checked={formData.agreeToPrivacy}
                      onChange={(e) => setFormData({ ...formData, agreeToPrivacy: e.target.checked })}
                      required
                      className="w-4 h-4 text-teal-500 focus:ring-teal-500 mt-1 cursor-pointer"
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      I agree to the <a href="/privacy-policy" className="text-teal-600 hover:text-teal-700 font-medium">Privacy Policy</a> and consent to the processing of my personal data for recruitment purposes. <span className="text-red-500">*</span>
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Submitting...
                    </span>
                  ) : (
                    'Submit Application'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions Section */}
      <section id="open-positions" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">Open Position</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Join our growing team and help us expand our presence in the European market.
            </p>
          </div>

          <div className="max-w-5xl mx-auto space-y-6">
            {jobPositions.map((job) => (
              <div
                key={job.id}
                className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => setSelectedJob(job)}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                  <div>
                    <h3 className="text-2xl font-bold mb-2">{job.title}</h3>
                    <div className="flex flex-wrap gap-3">
                      <span className="inline-flex items-center gap-2 text-gray-600">
                        <i className="ri-building-line"></i>
                        {job.department}
                      </span>
                      <span className="inline-flex items-center gap-2 text-gray-600">
                        <i className="ri-map-pin-line"></i>
                        {job.location}
                      </span>
                      <span className="inline-flex items-center gap-2 text-gray-600">
                        <i className="ri-time-line"></i>
                        {job.type}
                      </span>
                    </div>
                  </div>
                  <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap">
                    View Details
                  </button>
                </div>
                <p className="text-gray-600 leading-relaxed">{job.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Details Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedJob(null)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold mb-2">{selectedJob.title}</h2>
                <div className="flex flex-wrap gap-3">
                  <span className="inline-flex items-center gap-2 text-gray-600">
                    <i className="ri-building-line"></i>
                    {selectedJob.department}
                  </span>
                  <span className="inline-flex items-center gap-2 text-gray-600">
                    <i className="ri-map-pin-line"></i>
                    {selectedJob.location}
                  </span>
                  <span className="inline-flex items-center gap-2 text-gray-600">
                    <i className="ri-time-line"></i>
                    {selectedJob.type}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedJob(null)}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-8">
              <div>
                <h3 className="text-xl font-bold mb-4">About Thyamis</h3>
                <p className="text-gray-600 leading-relaxed mb-4">{selectedJob.description}</p>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4">Role Overview</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-circle-fill text-teal-500 text-xl mt-0.5 flex-shrink-0"></i>
                    <span className="text-gray-600 leading-relaxed">Identify and engage potential customers</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-circle-fill text-teal-500 text-xl mt-0.5 flex-shrink-0"></i>
                    <span className="text-gray-600 leading-relaxed">Develop and execute structured sales and marketing plans</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-circle-fill text-teal-500 text-xl mt-0.5 flex-shrink-0"></i>
                    <span className="text-gray-600 leading-relaxed">Convert leads into long-term clients and manage SMB portfolios</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-arrow-right-circle-fill text-teal-500 text-xl mt-0.5 flex-shrink-0"></i>
                    <span className="text-gray-600 leading-relaxed">Ensure sales targets are consistently met in dynamic startup environment</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4">Key Responsibilities</h3>
                <ul className="space-y-3">
                  {selectedJob.responsibilities.map((resp, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="ri-arrow-right-circle-fill text-blue-500 text-xl mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-600 leading-relaxed">{resp}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4">Requirements</h3>
                <ul className="space-y-3">
                  {selectedJob.requirements.map((req, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-teal-500 text-xl mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-600 leading-relaxed">{req}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4">What We Offer</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-star-fill text-teal-500 text-xl mt-0.5 flex-shrink-0"></i>
                    <span className="text-gray-600 leading-relaxed">Competitive salary + performance-based bonuses</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-star-fill text-teal-500 text-xl mt-0.5 flex-shrink-0"></i>
                    <span className="text-gray-600 leading-relaxed">Remote-first work environment with flexible hours</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-star-fill text-teal-500 text-xl mt-0.5 flex-shrink-0"></i>
                    <span className="text-gray-600 leading-relaxed">Opportunity to grow into a senior leadership role as the company expands</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-star-fill text-teal-500 text-xl mt-0.5 flex-shrink-0"></i>
                    <span className="text-gray-600 leading-relaxed">Direct collaboration with the founders to shape the future of Thyamis</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-star-fill text-teal-500 text-xl mt-0.5 flex-shrink-0"></i>
                    <span className="text-gray-600 leading-relaxed">High-impact role with autonomy and creativity</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-4">How to Apply</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Send your CV and a brief motivation to <strong className="text-teal-600">info@thyamis.com</strong> with the subject line: <strong>Solutions Engineer - Enterprise</strong>
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  Keywords: B2B Sales • European Market • CRM • Lead Generation
                </p>
                <a
                  href="mailto:info@thyamis.com?subject=Solutions Engineer - Enterprise"
                  className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
                >
                  Apply Now
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="relative py-24 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/2f7102ba1bcef2b8369bef774bca35e3/f7c1f7a3e03d65778b044259b5df78cd.png"
            alt="Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Don't See the Right Position?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              We're always looking for talented individuals. Send us your resume and we'll keep you in mind for future opportunities.
            </p>
            <a
              href="mailto:info@thyamis.com"
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors text-lg font-semibold whitespace-nowrap"
            >
              <i className="ri-mail-line text-xl"></i>
              Send Your Resume
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 mt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Join Our Team</h2>
            <p className="text-lg text-gray-200 leading-relaxed">
              We're always looking for passionate professionals to join our growing team. If you're interested in a role that isn't listed, send us your resume and let us know how you can contribute.
            </p>
            <a
              href="mailto:info@thyamis.com"
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              Send Your Resume
            </a>
          </div>
        </div>
      </footer>

      <Footer />
    </div>
  );
}
